import { useId, useLayoutEffect, useRef, useState } from 'react';
import { weekLabel } from '@/modules/projects/lib/weekLabel';

/**
 * The Gap Analysis charts (#396) — the four visuals from the manpower lead's workbook,
 * rebuilt as inline SVG. There is no charting library in this app and this
 * adds none.
 *
 * ── The palette, and why it is not a matter of taste ────────────────────────
 * Two roles carry one hue each, everywhere, so the reader learns them once:
 *
 *   NEEDED    (demand, measured from the crew schedule)  — quality blue, bars
 *   AVAILABLE (supply, typed by hand)                    — brand green, line
 *
 * Bars vs line is a second, independent encoding of the same distinction, so
 * identity never rests on color alone. Both were checked against the CVD and
 * contrast rules rather than eyeballed: blue↔green separates at ΔE 20 under
 * deutan, and the pair passes the lightness, chroma and 3:1 contrast checks on
 * this surface. Its tritan separation is 6.9, inside the band that is only
 * legal WITH secondary encoding — which the bar/line split, the legend and the
 * direct labels supply three times over.
 *
 * The gap-trend chart adds performance orange as a third identity. That trio
 * passes every check too, with one contrast warning on the orange, answered by
 * direct labels and by the table underneath carrying every value.
 *
 * ⚠️ Colors follow the ROLE, never the series' position. Changing the horizon
 * or reordering must never repaint a line.
 */

const NEEDED = 'var(--ew-quality)';      // #1875BD
const AVAILABLE = 'var(--ew-green)';     // #027835
const THIRD = 'var(--ew-performance)';   // #DB7A25

/**
 * POTENTIAL (SYSTAC pursuits — work we might win) — caution yellow, stacked.
 *
 * ⚠️ A FOURTH IDENTITY, added deliberately and encoded three ways. Yellow was
 * asked for by name, and it earns its place here because it is the one hue on
 * this surface that already means "not settled yet" — it is the safety/caution
 * token, used for warnings everywhere else in the app.
 *
 * Its weakness is contrast: #EBDB04 on white is far too light to carry meaning
 * alone, and it is the classic deutan/protan collision with the performance
 * orange the gap-trend chart uses. So it never appears alone and never on the
 * same chart as the orange: it is (1) a STACKED SEGMENT, structurally distinct
 * from the blue it sits on, (2) outlined in the darker safety-700 so its edge
 * survives on white and in grayscale, and (3) behind a toggle whose label names
 * it in words. The table underneath carries every value either way.
 */
const POTENTIAL = 'var(--ew-safety)';        // #EBDB04
const POTENTIAL_EDGE = 'var(--ew-safety-700)'; // #8A7503

const GRID = 'var(--ew-n-100)';
const AXIS_INK = 'var(--ew-n-500)';

export interface ChartSeries {
  key: string;
  label: string;
  color: string;
  /** Bars sit behind lines; one chart may hold both. */
  kind: 'bar' | 'line';
  /** One value per week. null renders as a break, never as zero. */
  values: (number | null)[];
  /**
   * Outline for a bar series whose fill is too light to hold an edge on white.
   * Carries the shape in grayscale and for a reader who cannot separate the
   * fill from its neighbor by hue.
   */
  edge?: string;
}

interface WeeklyChartProps {
  title: string;
  /** Y-axis caption, e.g. "Headcount". */
  unit: string;
  weekStarts: string[];
  series: ChartSeries[];
  /** Gap charts cross zero; headcount charts do not. */
  includeNegative?: boolean;
  /**
   * Bar series stack into one column instead of standing side by side.
   *
   * ⚠️ Stacking asserts that the parts ADD UP to something meaningful, so it is
   * opt-in rather than the default. It is right for measured demand plus the
   * SYSTAC potential — one is the work we have, the other the work we might
   * win, and their sum is the headcount the week would want. It would be wrong
   * for, say, needed against available, which are two readings of the same
   * quantity and must stay side by side.
   */
  stackBars?: boolean;
  /**
   * The series the Y AXIS is measured against, when that is not the series
   * being drawn.
   *
   * ⚠️ THIS EXISTS SO THE SYSTAC TOGGLE CANNOT RESCALE THE CHART. Stacking the
   * potential on top of measured demand raises the tallest column, and a scale
   * refitted to that new total shrinks every blue bar to make room — so turning
   * the pursuits ON made the measured demand look SMALLER, and the difference
   * the band exists to show got flattened out of the picture. From review:
   * "that gives the false illusion that the bars or difference is not big at
   * all."
   *
   * ⚠️ AND THE SCALE THAT GETS PASSED IS THE **WITH-PURSUITS** ONE, IN BOTH
   * STATES — the chart opens with the headroom it will need, rather than
   * fitting the measured bars and cutting the yellow off later. The blue bars
   * are then identical whichever way the box is set, nothing is ever clipped,
   * and the cost is paid where it does no harm: a little empty chart above the
   * bars while the pursuits are hidden. (from review: "do not cut the bars that hit
   * the ceiling but start with the scale the visuals would have when the SYSTAC
   * numbers are showing.")
   *
   * ⚠️ SO THIS MUST COVER EVERYTHING THE CHART DRAWS. Nothing here clamps a bar
   * to the plot: a series taller than its scale is silently clipped by the plot
   * mask. The caller passes the combined figure the API computes precisely so
   * that cannot happen.
   */
  scaleSeries?: ChartSeries[];
}

/**
 * ⚠️ THE DRAWING IS MEASURED, NOT GUESSED. The svg is `width="100%"` over its
 * viewBox, so the browser scales the whole drawing by (rendered width ÷ viewBox
 * width) — TEXT INCLUDED. Every font size below is therefore a lie unless the
 * viewBox matches the box the svg actually occupies. So WeeklyChart observes
 * its own container and draws at exactly that many units: scale 1.000, type at
 * the size it says, and no horizontal scrollbar at any width that can hold the
 * floor.
 *
 * This replaced a hard-coded 600. That constant had to be kept in step with the
 * grid's `minmax` by hand, and it had already drifted: the arithmetic in
 * GapAnalysisView's comment counted the grid track (~598px at 1920) but not the
 * card's own 16px padding and 1px border, so the svg only ever got ~564px of it
 * and every chart on a 1920 screen carried a scrollbar it was not supposed to
 * have. Measuring removes the coupling rather than re-tuning it.
 *
 * ⚠️ MIN_VIEW_W IS A READABILITY FLOOR, and it is the reason this is not simply
 * `width: 100%` with no viewBox management. Below it the drawing scrolls inside
 * its box instead of squeezing, because a uniform squeeze is what took the axis
 * labels to ~6px on a phone (Codex on #489).
 *
 * 460 is chosen against the grid, not picked: the narrowest track `minmax(min(
 * 520px, 100%), 1fr)` can hand out while still multi-column is 520px, which
 * leaves 486px inside the card — so no desktop layout can ever fall under the
 * floor, and no desktop layout scrolls. Below 520px the track collapses to the
 * container and a phone scrolls, as before, but 145px at 375px rather than the
 * 285px the old 600 floor forced.
 */
const MIN_VIEW_W = 460;
const PLOT_H = 220;
const AXIS_BAND = 30;
/** Room for a three-digit y tick at 12px. */
const PAD_L = 44;
const PAD_R = 10;
/** Breathing room inside the plot so a peak label never leaves the box and an
 *  extreme value never sits welded to the axis line. */
const TOP_PAD = 18;
const BOT_PAD = 6;

/** Type sizes, in the same units as everything else — see MIN_VIEW_W. */
const TICK_FS = 12;
const PEAK_FS = 12.5;

/**
 * Round up to something a human would choose for a top gridline.
 *
 * The step list is deliberately fine-grained: with only [1, 2, 2.5, 5] a peak
 * of 143 scaled to 200 and left the bars looking becalmed in 40% of empty
 * chart. 1.5 and 3 keep the marks filling the box.
 */
function niceCeil(v: number): number {
  if (v <= 5) return 5;
  const mag = Math.pow(10, Math.floor(Math.log10(v)));
  for (const step of [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10]) {
    const candidate = step * mag;
    if (candidate >= v) return candidate;
  }
  return 10 * mag;
}

export function WeeklyChart({ title, unit, weekStarts, series, includeNegative, stackBars, scaleSeries }: WeeklyChartProps) {
  const [hover, setHover] = useState<number | null>(null);
  const clipId = useId();

  /**
   * The svg's own width, in the units it draws in — so scale stays 1.000.
   *
   * ⚠️ Seeded at the floor rather than 0, so a chart rendered before it can be
   * measured — in a test environment without layout, or a browser with no
   * ResizeObserver — is a real chart at a sane size rather than a collapsed one.
   * It is corrected to the true width below, before the first paint.
   */
  const boxRef = useRef<HTMLDivElement>(null);
  const [viewW, setViewW] = useState(MIN_VIEW_W);

  /**
   * ⚠️ useLayoutEffect, and the FIRST measurement is a direct read rather than
   * the observer's. Both halves matter:
   *
   * - Direct, because ResizeObserver's initial delivery is pre-paint work the
   *   browser suspends while a tab is hidden or backgrounded. A chart mounted
   *   in a background tab would otherwise sit at the floor until the tab was
   *   looked at. Reading clientWidth here cannot be suspended. (Caught while
   *   verifying this: a hidden preview pane delivered no observations at all,
   *   including the guaranteed first one.)
   * - Layout, not passive, because a passive effect runs after paint, so the
   *   floor-sized chart would be painted once and then jump to its real width.
   */
  useLayoutEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    // ⚠️ Rounded, and only committed when it actually changes. A subpixel
    // container width would otherwise rewrite the viewBox on every observer
    // callback and re-render the chart forever.
    const apply = (w: number) => {
      const next = Math.max(MIN_VIEW_W, Math.round(w));
      setViewW((prev) => (prev === next ? prev : next));
    };
    apply(el.clientWidth);
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => apply(entries[0].contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const n = weekStarts.length;
  const bars = series.filter((s) => s.kind === 'bar');
  const lines = series.filter((s) => s.kind === 'line');

  /** Column-wise totals of a set of bar series — the height a stack reaches. */
  const totalsOf = (ss: ChartSeries[]) =>
    Array.from({ length: n }, (_, i) =>
      ss.filter((s) => s.kind === 'bar').reduce((sum, s) => sum + (s.values[i] ?? 0), 0));
  const stackTotals = stackBars ? totalsOf(bars) : [];

  // ⚠️ The scale has to see the STACK, not its tallest part. Scaling to the
  // largest single value clipped the top segment clean off the plot the moment
  // two bars were stacked.
  //
  // ⚠️ …but it sees `scaleSeries` when one is given, which is how the SYSTAC
  // toggle is stopped from rescaling the chart. See the prop.
  const measured = scaleSeries ?? series;
  const flat = [
    ...measured.flatMap((s) => s.values.filter((v): v is number => v !== null)),
    ...(stackBars ? totalsOf(measured) : []),
  ];
  const rawMax = flat.length ? Math.max(...flat) : 0;
  const rawMin = flat.length ? Math.min(...flat) : 0;

  const top = niceCeil(Math.max(rawMax, 1));
  const bottom = includeNegative && rawMin < 0 ? -niceCeil(Math.abs(rawMin)) : 0;
  const span = top - bottom || 1;

  const plotW = viewW - PAD_L - PAD_R;
  const colW = plotW / Math.max(n, 1);
  const bandH = PLOT_H - TOP_PAD - BOT_PAD;
  const y = (v: number) => TOP_PAD + (1 - (v - bottom) / span) * bandH;
  const cx = (i: number) => PAD_L + colW * i + colW / 2;

  // Ticks: bottom, zero (when the scale crosses it), and top.
  const ticks = Array.from(new Set([bottom, 0, top].filter((t) => t >= bottom && t <= top)));

  // 2px of surface either side of a bar — a gap, never a border. A stack
  // occupies ONE slot however many segments it holds.
  const barSlots = stackBars ? 1 : Math.max(bars.length, 1);
  const barW = Math.max(3, Math.min(26, colW / barSlots - 4));

  /** Height of the bar series below `si` in column `i` — the stack's base. */
  const stackBase = (si: number, i: number) =>
    stackBars ? bars.slice(0, si).reduce((sum, s) => sum + (s.values[i] ?? 0), 0) : 0;

  /** The topmost segment carries the rounded end; the ones under it are square. */
  const topSegment = (i: number) => {
    for (let k = bars.length - 1; k >= 0; k--) if ((bars[k].values[i] ?? 0) > 0) return k;
    return -1;
  };

  /** Label only the extreme, never every point. */
  const peakIndex = (vals: (number | null)[]) => {
    let best = -1, bestV = -Infinity;
    vals.forEach((v, i) => { if (v !== null && v > bestV) { bestV = v; best = i; } });
    return best;
  };

  /**
   * Each series' peak, placed — above its mark, or under it when the label
   * above would land on one already there.
   *
   * ⚠️ TWO SERIES PEAKING IN THE SAME WEEK A FEW UNITS APART OVERPRINT, and the
   * result is not a crowded label, it is an unreadable one: 81 over 76 renders
   * as a smudge that is neither. It stayed theoretical while the gap lines were
   * far apart and became real the moment they were computed with the SYSTAC
   * pursuits included, which lifts Total and Traveler onto the same peak week.
   *
   * The taller value keeps the conventional spot, so the topmost number on the
   * chart is still the one above the topmost mark; the shorter one flips under
   * its own marker rather than being nudged further up, because up is where the
   * plot ceiling is and a label pushed through it is the problem again.
   *
   * ⚠️ EVERY LABEL GETS A POSITION OF ITS OWN, VALIDATED — not a preferred spot
   * and one shared fallback. Three series peaking in one week is the case that
   * proves the difference: the second and third both find the spot above taken
   * and, given a single alternative, both take THAT, ending a few units apart.
   * The candidates are tried in order and the last resort is derived from what
   * is already placed, so it cannot collide however many series there are.
   */
  const peakLabels = (() => {
    const wanted = (stackBars ? series.filter((s) => s.kind === 'line') : series)
      .map((s) => ({ s, i: peakIndex(s.values) }))
      .map(({ s, i }) => ({ s, i, v: i >= 0 ? s.values[i] : null }))
      // A peak of zero is not worth labeling — it just parks a "0" on the axis
      // of an empty chart.
      .filter((p): p is { s: ChartSeries; i: number; v: number } =>
        p.i >= 0 && p.v !== null && p.v !== 0);

    const SPACING = PEAK_FS + 2;
    const takenByColumn = new Map<number, number[]>();
    // Tallest first, so it is the one that gets the spot above the mark.
    return [...wanted].sort((a, b) => b.v - a.v).map(({ s, i, v }) => {
      const taken = takenByColumn.get(i) ?? [];
      const free = (p: number) => taken.every((t) => Math.abs(t - p) >= PEAK_FS);

      // ⚠️ EVERY CANDIDATE IS CHECKED, INCLUDING THE FALLBACK. Testing only the
      // preferred spot and then dropping to a fixed alternative is what broke
      // three-way collisions: with 81, 78 and 76 all peaking in one week, the
      // last two both found "above" occupied and both took the same
      // below-the-mark position, landing three units apart — one unreadable
      // pair traded for another. (Codex on #500.)
      const candidates = [
        y(v) - 10,          // the conventional spot, above the mark
        y(v) + SPACING,     // under its own mark, so it does not sit on a marker
      ];
      // …and a last resort that CANNOT collide, whatever is already placed:
      // one clear space past the lowest label in the column, or past the
      // highest when there is no room left below the plot.
      const under = Math.max(...taken) + SPACING;
      const over = Math.min(...taken) - SPACING;
      const ly = candidates.find(free) ?? (under <= PLOT_H - 2 ? under : over);

      taken.push(ly);
      takenByColumn.set(i, taken);
      return { key: s.key, x: cx(i), ly, v };
    });
  })();

  // Week labels thin out as the horizon grows, so they cannot collide.
  const labelEvery = n > 18 ? 4 : n > 10 ? 2 : 1;

  return (
    // mp-chart-card: the print stylesheet keeps the whole figure on one sheet
    // and lets the svg shrink to the page. A chart split across a page break is
    // half an axis on one sheet and half a legend on the next, and the scroll
    // floor that keeps the type readable on screen is what pushed the drawing
    // off the right-hand edge on paper.
    <figure className="mp-chart-card" style={{ margin: 0, background: 'var(--ew-white, #fff)', border: '1px solid var(--ew-n-100)', borderRadius: 'var(--ew-r-lg)', padding: '14px 16px 12px', position: 'relative' }}>
      <figcaption style={{ font: 'var(--ew-fw-semibold) 14px/1.3 var(--ew-font)', color: 'var(--ew-dark)', marginBottom: 2 }}>
        {title}
        <span style={{ display: 'block', font: 'var(--ew-fw-regular) 11.5px/1.4 var(--ew-font)', color: 'var(--ew-n-500)' }}>
          {unit}
        </span>
      </figcaption>

      {/* Legend — always present for two or more series, so identity never
          rests on hue alone. */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', margin: '4px 0 6px' }}>
        {series.map((s) => (
          <span key={s.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--ew-n-500)' }}>
            <span aria-hidden style={{
              width: s.kind === 'bar' ? 9 : 14,
              height: s.kind === 'bar' ? 9 : 2.5,
              borderRadius: s.kind === 'bar' ? 2 : 2,
              background: s.color,
              display: 'inline-block',
            }} />
            {s.label}
          </span>
        ))}
      </div>

      {/* ⚠️ THIS BOX IS THE RULER as well as the scroll container — the svg draws
          in exactly as many units as this div is wide. `width="100%"` on its own
          never overflows, but the viewBox then scales the whole drawing
          uniformly, so on a phone the axis labels came out at roughly half size:
          unreadable rather than merely small. Measuring keeps scale at 1.000
          instead of choosing between squeezing and scrolling, and below
          MIN_VIEW_W the floor takes over and the overflow becomes a scroll
          inside this box rather than the page shifting sideways. */}
      <div ref={boxRef} style={{ overflowX: 'auto' }}>
      <svg
        viewBox={`0 0 ${viewW} ${PLOT_H + AXIS_BAND}`}
        width="100%"
        height={PLOT_H + AXIS_BAND}
        role="img"
        aria-label={`${title}. ${unit} by week. Values are also in the table below.`}
        // ⚠️ The floor and the viewBox are the SAME number whenever the box is
        // narrower than the floor, which is what makes the overflow a scroll
        // rather than a squeeze. Everywhere else viewW is the box's own width,
        // so this minWidth is slack and no scrollbar appears at all.
        //
        // The predecessor pinned this at a fixed 600 and every chart on a 1920
        // screen scrolled ~36px, because the card's padding and border eat 34px
        // the grid arithmetic never counted. Widths are measured now, so that
        // class of drift cannot come back. (Reported on #498.)
        style={{ display: 'block', overflow: 'visible', minWidth: MIN_VIEW_W }}
        onMouseLeave={() => setHover(null)}
      >
        <defs>
          <clipPath id={clipId}><rect x={PAD_L} y={-4} width={plotW} height={PLOT_H + 8} /></clipPath>
        </defs>

        {/* Recessive grid: solid hairlines, one shade off the surface. Never
            dashed. On a chart that crosses zero the zero line is the threshold
            the reader is actually looking for, so it is one shade stronger. */}
        {ticks.map((t) => (
          <g key={t}>
            <line x1={PAD_L} x2={viewW - PAD_R} y1={y(t)} y2={y(t)}
                  stroke={t === 0 && bottom < 0 ? 'var(--ew-n-300)' : GRID} strokeWidth={1} />
            <text x={PAD_L - 6} y={y(t) + 3.5} textAnchor="end" fontSize={TICK_FS} fill={AXIS_INK} style={{ fontVariantNumeric: 'tabular-nums' }}>{t}</text>
          </g>
        ))}

        {/* Hover bands. Full height and at least the column's width, so the hit
            target is nowhere near the size of the mark. */}
        {weekStarts.map((ws, i) => (
          <rect
            key={ws}
            x={PAD_L + colW * i} y={0} width={colW} height={PLOT_H}
            fill={hover === i ? 'var(--ew-n-50)' : 'transparent'}
            onMouseEnter={() => setHover(i)}
          />
        ))}

        <g clipPath={`url(#${clipId})`}>
          {bars.map((s, si) => s.values.map((v, i) => {
            if (v === null) return null;
            const groupW = stackBars ? barW : barW * bars.length;
            const x = cx(i) - groupW / 2 + (stackBars ? 0 : si * barW);
            const base = stackBase(si, i);
            const yTop = y(base + Math.max(v, 0));
            const h = Math.abs(y(base + v) - y(base));
            if (h < 0.5) return null;
            // ⚠️ AN OUTLINED BAR HAS TO BE INSET BY HALF ITS STROKE, or it is
            // wider than its neighbors. An SVG stroke straddles the path, so a
            // 1px outline on a path spanning `barW` paints barW + 1 — and the
            // yellow segment sat visibly proud of the blue column under it.
            // Insetting left, right and top puts the stroke's OUTER edge back on
            // the same footprint an unstroked bar occupies.
            //
            // The bottom is deliberately NOT inset: it meets the segment below,
            // and pulling it up half a pixel opens a pale seam along the join.
            // Left flush, the outline reads as the divider between the two.
            const sw = s.edge ? 1 : 0;
            const bx = x + sw / 2;
            const bw = Math.max(0.5, barW - sw);
            const bTop = yTop + sw / 2;
            const bBot = yTop + h;
            // 4px rounded data-end, anchored square to the baseline — and, in a
            // stack, only on the segment that IS the end. Rounding every
            // segment would carve a notch between them and read as a gap in a
            // quantity that is continuous.
            const r = (!stackBars || si === topSegment(i)) ? Math.min(4, bw / 2, bBot - bTop) : 0;
            return (
              <path
                key={`${s.key}-${i}`}
                d={r > 0
                  ? `M${bx},${bBot} V${bTop + r} Q${bx},${bTop} ${bx + r},${bTop} H${bx + bw - r} Q${bx + bw},${bTop} ${bx + bw},${bTop + r} V${bBot} Z`
                  : `M${bx},${bBot} V${bTop} H${bx + bw} V${bBot} Z`}
                fill={s.color}
                stroke={s.edge}
                strokeWidth={sw || undefined}
                opacity={hover === null || hover === i ? 1 : 0.45}
              />
            );
          }))}

          {lines.map((s) => {
            const pts = s.values.map((v, i) => (v === null ? null : [cx(i), y(v)] as const));
            // A null breaks the line rather than being drawn as zero.
            const d = pts.reduce((acc, p, i) => {
              if (!p) return acc;
              const prev = i > 0 ? pts[i - 1] : null;
              return acc + (prev ? ` L${p[0]},${p[1]}` : ` M${p[0]},${p[1]}`);
            }, '');
            return (
              <g key={s.key}>
                <path d={d.trim()} fill="none" stroke={s.color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                {pts.map((p, i) => p && (
                  // 2px surface ring keeps a marker legible where it overlaps a bar.
                  <circle key={i} cx={p[0]} cy={p[1]} r={hover === i ? 5 : 4}
                          fill={s.color} stroke="var(--ew-white, #fff)" strokeWidth={2} />
                ))}
              </g>
            );
          })}

          {/* Selective direct labels: the peak of each series, and nothing else.
              ⚠️ In a STACK the bar series are labeled once, on the stack total.
              Labelling each segment would print the yellow's own height next to
              a bar whose top is the combined figure — two numbers, neither of
              which is what the bar reaches. Lines keep their own labels. */}
          {peakLabels.map(({ key, x, ly, v }) => (
            <text key={`peak-${key}`} x={x} y={ly} textAnchor="middle"
                  fontSize={PEAK_FS} fontWeight={700} fill="var(--ew-dark)">
              {v}
            </text>
          ))}

          {stackBars && (() => {
            const i = peakIndex(stackTotals);
            const v = i >= 0 ? stackTotals[i] : 0;
            if (!v) return null;
            return (
              <text x={cx(i)} y={y(v) - 10} textAnchor="middle"
                    fontSize={PEAK_FS} fontWeight={700} fill="var(--ew-dark)">
                {v}
              </text>
            );
          })()}
        </g>

        {/* X axis: a solid hairline and thinned week labels. */}
        <line x1={PAD_L} x2={viewW - PAD_R} y1={PLOT_H} y2={PLOT_H} stroke="var(--ew-n-200)" strokeWidth={1} />
        {weekStarts.map((ws, i) => i % labelEvery === 0 && (
          <text key={ws} x={cx(i)} y={PLOT_H + 16} textAnchor="middle" fontSize={TICK_FS} fill={AXIS_INK}>
            {weekLabel(ws)}
          </text>
        ))}
        {/* ⚠️ The unit caption is NOT drawn here. Sitting at the plot's bottom
            left it landed directly under the first week label and the two
            overlapped; it lives in the header instead. */}
      </svg>
      </div>

      {hover !== null && (
        <div role="status" style={{
          position: 'absolute', top: 8, right: 10, pointerEvents: 'none',
          background: 'var(--ew-dark)', color: 'var(--ew-white, #fff)',
          borderRadius: 'var(--ew-r-md)', padding: '7px 10px', fontSize: 12, lineHeight: 1.5,
          boxShadow: '0 2px 8px rgba(0,0,0,0.18)', minWidth: 120,
        }}>
          <div style={{ fontWeight: 700, marginBottom: 2 }}>Week of {weekLabel(weekStarts[hover])}</div>
          {series.map((s) => (
            <div key={s.key} style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
              <span style={{ opacity: 0.85 }}>
                <span aria-hidden style={{ display: 'inline-block', width: 7, height: 7, borderRadius: 2, background: s.color, marginRight: 5 }} />
                {s.label}
              </span>
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                {s.values[hover] === null ? '—' : s.values[hover]}
              </span>
            </div>
          ))}
        </div>
      )}
    </figure>
  );
}

export const CHART_COLORS = { NEEDED, AVAILABLE, THIRD, POTENTIAL, POTENTIAL_EDGE };
